/**
 * This browser's World pointer (RFC 0261 slice 1).
 *
 * The World is a DEPLOYMENT's directory of projects. A client reaches one
 * through a pointer naming which World document it uses — a file on cli-next
 * (`~/.mod/next-world.json`), this localStorage key in the browser, UserDefaults
 * on iOS. That indirection is what lets a World MOVE: put the same url in two
 * clients and they read one directory.
 *
 * Why this matters here specifically: before the World, a missing project key
 * meant web-next created a BRAND-NEW project on the spot. Clearing storage or
 * opening a second browser silently forked an empty project with no route back
 * to the real one. The World is the durable list that makes "restore" possible,
 * so creating is a last resort rather than the default.
 *
 * Note the split in roles: the World says which projects EXIST; the existing
 * `PROJECT_KEY` still says which one this browser had open last.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import {
  createWorld,
  privateSessionWorldId,
  usesPrivateSessionCatalog,
} from '@mod/core/model'
import { noteWorldCreated } from '../../shell/window/onboarding/onboardingState'
import { desktopDirectRequested } from './repo/desktop-direct'

/** localStorage key: which World document this browser uses. */
export const WORLD_KEY = 'mod_newsync_world'
let privateRepo: Repo | undefined
/** With sharing on, the signed-in account names the World, not a pointer.
 *  Not in the desktop window: the daemon signed in, and the window is its
 *  loopback peer (RFC 0345 3a). */
export const sharingEnabled = () =>
  (import.meta.env.VITE_MOD_KEYHIVE === '1' && !desktopDirectRequested()) ||
  !!privateRepo

/** Read this browser's World id, or null if it has never created/joined one.
 *  Never throws — localStorage is unavailable in some privacy modes, and that
 *  must degrade to "no World yet" rather than break the app shell. */
export function readWorldPointer(): string | null {
  if (sharingEnabled())
    return privateRepo ? (privateSessionWorldId(privateRepo) ?? null) : null
  try {
    const raw = localStorage.getItem(WORLD_KEY)
    if (!raw) return null
    return raw.replace(/^automerge:/, '')
  } catch {
    return null
  }
}

/** Point this browser at a World — the join half of moving one between devices. */
export function writeWorldPointer(worldIdOrUrl: string): void {
  if (sharingEnabled())
    throw new Error(
      'Sharing Worlds must be opened through account enrollment or recovery',
    )
  try {
    localStorage.setItem(WORLD_KEY, worldIdOrUrl.replace(/^automerge:/, ''))
  } catch {
    // best-effort; the World still exists in-repo for this session
  }
}

/**
 * Open this browser's World, creating it (and the pointer naming it) ONLY on
 * first use.
 *
 * A POINTER IS NEVER REPLACED BECAUSE THE DOCUMENT DID NOT OPEN. This used to
 * `repo.find` the stored id and, on any failure, mint a fresh World over the
 * top — justified as recovering from a stale pointer, and the comment even
 * named the trigger: "a previous run can write a pointer and exit before the
 * document finishes syncing". That is precisely the case where the right answer
 * is to WAIT. A World that has not arrived yet is indistinguishable from one
 * that does not exist, and guessing "does not exist" costs you the directory of
 * every project you have: the old World is stranded, still holding them, while
 * this browser starts an empty one and quietly diverges from the daemon.
 *
 * It is the same mistake `resolveSpace` is written to avoid one level down, and
 * the same one `WorldPage` already carries a comment about for projects.
 * Absent is not empty, at every level of this pointer chain.
 *
 * So the id is returned as soon as it is known. Whether the document has synced
 * is a separate question, and the readers already answer it: `useWorldProjects`
 * projects an unread World as `[]` rather than throwing.
 */
export async function getOrCreateWorld(
  repo: Repo,
): Promise<{ worldId: string; created: boolean }> {
  if (usesPrivateSessionCatalog(repo) || sharingEnabled()) {
    const worldId = privateSessionWorldId(repo)
    if (!worldId) throw new Error('Open your account before opening its World')
    if (privateRepo && privateRepo !== repo)
      throw new Error('Reload before switching sharing accounts')
    privateRepo = repo
    return { worldId, created: false }
  }
  const existing = readWorldPointer()
  if (existing) return { worldId: existing, created: false }
  const handle = createWorld(repo)
  const worldId = handle.documentId as string
  writeWorldPointer(worldId)
  // A brand-new World means nobody has used Mod in this browser, which is the
  // trigger for onboarding. Flagged here rather than at the two call sites so
  // neither can forget, and so a caller that creates a World for its own reasons
  // still arms the flow. Best-effort and idempotent; see noteWorldCreated.
  noteWorldCreated()
  return { worldId, created: true }
}
